import React, { useContext } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import LoadingAnimation from '../components/common/LoadingAnimation';
import { useTranslation } from '../hooks/useTranslation';
import {
  isChef,
  canAccessManageRestaurant,
  canCreateUsers,
  canAccessBranches,
  canAccessQRMenu,
  hasBranchAdminAccess
} from '../utils/permissionHelpers';

const PrivateRoute = () => {
  const { currentUser, isLoading, needsOnboarding } = useContext(AuthContext);
  const location = useLocation();
  const pathname = location.pathname || '';
  const { t } = useTranslation();

  if (isLoading) {
    return <LoadingAnimation fullPage message={t('common.loading')} />;
  }

  if (!currentUser) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Owners/admins with no restaurant yet are kept on the setup flow until
  // they finish it (or skip the final step) — desktop app is exempt since
  // it has its own minimal sign-in/POS flow.
  if (
    needsOnboarding &&
    !pathname.startsWith('/onboarding') &&
    !(typeof window !== 'undefined' && window.electronAPI?.isElectron)
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // Electron: the desktop app has two screens — sign-in and POS. Users with a
  // branch are kept on the POS screen; users without one (e.g. owners) keep
  // the dashboard so they can pick a branch.
  if (typeof window !== 'undefined' && window.electronAPI?.isElectron) {
    if (currentUser.branchId && !pathname.startsWith('/pos')) {
      return <Navigate to={`/pos/${currentUser.branchId}`} replace />;
    }
    if (
      pathname.startsWith('/branches') ||
      pathname.startsWith('/settings')
    ) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (isChef(currentUser) && !pathname.startsWith('/pos')) {
    const to = currentUser.branchId ? `/pos/${currentUser.branchId}` : '/pos';
    return <Navigate to={to} replace />;
  }
  if (pathname.startsWith('/manage-restaurant') && !canAccessManageRestaurant(currentUser)) {
    return <Navigate to={currentUser.branchId ? `/pos/${currentUser.branchId}` : '/dashboard'} replace />;
  }
  if (pathname.startsWith('/users') && !canCreateUsers(currentUser)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (pathname.startsWith('/branches') && !pathname.includes('/assigned-') && !canAccessBranches(currentUser)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (pathname.startsWith('/qr-menu') && !canAccessQRMenu(currentUser)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (pathname.startsWith('/assistant') && !hasBranchAdminAccess(currentUser)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

export default PrivateRoute;